import { useState } from "react";
import { ShieldAlert, Wrench, CheckCircle2 } from "lucide-react";
import {
    ENGINEER_CHECKS,
    PROVIDER_CHECKS,
    INSTALL_RESULTS_BLOCKED,
    INSTALL_RESULTS_CLEAN,
    EXCEPTION_CHAIN,
    CLINICAL_REVIEW,
    AI_LINE,
} from "./data";
import { StatusChip, Kicker, ChainRow, ActionBtn, LocalBoundary } from "./ui";

const STAGE_CHIP = {
    start: ["slate", "AWAITING WORK-ORDER ACCEPTANCE"],
    authorised: ["blue", "INSTALLATION AUTHORISED"],
    testing: ["blue", "TECHNICAL TESTING"],
    blocked: ["red", "BLOCKED — INTERFACE TEST FAILED"],
    accepted: ["green", "TECHNICALLY ACCEPTED"],
    released: ["green", "RELEASED TO SERVICE"],
    denied: ["red", "DENIED — NO ACCEPTABLE CLINICAL CONTINGENCY"],
    rolledback: ["red", "ROLLED BACK — POST-INSTALLATION HEALTH CHECK FAILED"],
};

export const ActInstall = ({ scenario, stage, setStage }) => {
    const [observationNote, setObservationNote] = useState(false);
    const [cameFromBlocked, setCameFromBlocked] = useState(false);
    const [chip, chipLabel] = STAGE_CHIP[stage] || STAGE_CHIP.start;

    if (scenario === "excluded") {
        return (
            <section data-testid="act-install">
                <Kicker>Act 4 — Installation and release</Kicker>
                <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                    Legacy device — no installation is attempted
                </h2>
                <div className="mt-6 rounded-sm border border-slate-300 bg-slate-100 p-6" data-testid="install-excluded-banner">
                    <StatusChip tone="slate" testId="install-state-chip">EXCLUDED — LEGACY DEVICE NOT SUPPORTED</StatusChip>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
                        The workstation runs the withdrawn legacy operating system and is
                        outside the applicable change package. No work order is raised, no
                        engineer is assigned and no installation gate can open. The device
                        remains in its current verified state while its replacement
                        decision is handled as a separate, locally owned journey.
                    </p>
                </div>
            </section>
        );
    }

    const runInstall = () => {
        if (scenario === "blocked") {
            setCameFromBlocked(true);
            setStage("blocked");
        } else {
            setStage("accepted");
        }
    };

    const reset = () => {
        setStage("start");
        setObservationNote(false);
        setCameFromBlocked(false);
    };

    const done = ["released", "denied", "rolledback"].includes(stage);
    const showResults = ["blocked", "accepted", "released", "denied", "rolledback"].includes(stage);
    const resultsRows = stage === "blocked" ? INSTALL_RESULTS_BLOCKED : INSTALL_RESULTS_CLEAN;

    return (
        <section data-testid="act-install">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <Kicker>Act 4 — Installation and release</Kicker>
                    <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                        A bounded pilot installation at the first site
                    </h2>
                </div>
                <button
                    onClick={reset}
                    data-testid="install-reset-btn"
                    className="rounded-sm border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                    Reset pilot journey
                </button>
            </div>
            <div className="mt-3">
                <StatusChip tone={chip} testId="install-state-chip">{chipLabel}</StatusChip>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {/* Engineer / Technology Partner */}
                <LocalBoundary testId="install-engineer-card" highlight={stage === "start"}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900">
                            Healthcare Technology Partner — authorised service engineer
                        </p>
                        <StatusChip tone={stage === "start" ? "slate" : "green"}>
                            {stage === "start" ? "Work order offered" : "Work order accepted"}
                        </StatusChip>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                        Engineer reference ENG-SYN-014 (synthetic). Checks gate the
                        acceptance — an engineer without a completed briefing cannot be
                        assigned.
                    </p>
                    <ul className="mt-3 grid grid-cols-2 gap-1 text-sm text-slate-600">
                        {ENGINEER_CHECKS.map((c) => (
                            <li key={c} className="flex items-center gap-1.5">
                                <CheckCircle2 className={`h-3.5 w-3.5 ${stage === "start" ? "text-slate-300" : "text-green-600"}`} strokeWidth={1.5} />
                                {c}
                            </li>
                        ))}
                    </ul>
                    {stage === "start" && (
                        <div className="mt-4">
                            <ActionBtn primary onClick={() => setStage("authorised")} testId="install-accept-workorder-btn">
                                <Wrench className="h-4 w-4" strokeWidth={1.5} />
                                Accept work order — authorised engineer
                            </ActionBtn>
                        </div>
                    )}
                </LocalBoundary>

                {/* Provider technical */}
                <LocalBoundary testId="install-provider-card" highlight={stage === "authorised"} dim={stage === "start"}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900">
                            Provider Clinical Engineering and IT — installation permission
                        </p>
                        <StatusChip tone={["start", "authorised"].includes(stage) ? "slate" : "green"}>
                            {["start", "authorised"].includes(stage) ? "Permission pending" : "Connection permitted"}
                        </StatusChip>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                        The provider — not the supplier or the partner — permits connection
                        and local testing in its own environment.
                    </p>
                    <ul className="mt-3 grid grid-cols-2 gap-1 text-sm text-slate-600">
                        {PROVIDER_CHECKS.map((c) => (
                            <li key={c} className="flex items-center gap-1.5">
                                <CheckCircle2 className={`h-3.5 w-3.5 ${["start", "authorised"].includes(stage) ? "text-slate-300" : "text-green-600"}`} strokeWidth={1.5} />
                                {c}
                            </li>
                        ))}
                    </ul>
                    {stage === "authorised" && (
                        <div className="mt-4">
                            <ActionBtn primary onClick={() => setStage("testing")} testId="install-permit-btn">
                                Permit connection and local testing — Provider IT
                            </ActionBtn>
                        </div>
                    )}
                </LocalBoundary>
            </div>

            {stage === "testing" && (
                <div className="mt-4 rounded-sm border border-slate-200 bg-white p-6" data-testid="install-run-panel">
                    <p className="text-sm leading-relaxed text-slate-600">
                        The engineer performs the bounded installation inside the permitted
                        window, with the verified rollback package staged on site.
                    </p>
                    <div className="mt-3">
                        <ActionBtn primary onClick={runInstall} testId="install-run-btn">
                            Run pilot installation and post-installation tests
                        </ActionBtn>
                    </div>
                </div>
            )}

            {showResults && (
                <div className="mt-4 rounded-sm border border-slate-200 bg-white p-6" data-testid="install-results-table">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Installation results</p>
                    <ul className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
                        {resultsRows.map(([label, value, tone]) => (
                            <li key={label} className="flex items-center justify-between py-2 text-sm">
                                <span className="text-slate-700">{label}</span>
                                <StatusChip tone={tone}>{value}</StatusChip>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {stage === "blocked" && (
                <div className="mt-4 rounded-sm border border-red-200 bg-red-50 p-6" data-testid="install-blocked-banner">
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-red-700" strokeWidth={1.5} />
                        <p className="font-mono text-sm font-medium text-red-700">BLOCKED — INTERFACE TEST FAILED</p>
                    </div>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-red-900/70">
                        Production release is blocked by the failed interoperability
                        interface test. The exception is assigned jointly to the
                        Healthcare Technology Partner and Provider IT. The block can only
                        clear through a new passing check — never by editing the status.
                    </p>
                    <div className="mt-4">
                        <ActionBtn primary onClick={() => setStage("accepted")} testId="install-correct-btn">
                            Correct configuration and re-run interface test
                        </ActionBtn>
                    </div>
                </div>
            )}

            {(stage === "accepted" || done) && cameFromBlocked && (
                <div className="mt-4 rounded-sm border border-slate-200 bg-white p-6" data-testid="install-exception-chain">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Exception resolution</p>
                    <div className="mt-3">
                        <ChainRow steps={EXCEPTION_CHAIN} activeIndex={EXCEPTION_CHAIN.length - 1} testIdPrefix="exception-chain" />
                    </div>
                    <p className="mt-3 text-xs text-slate-500">
                        The corrected configuration passed a fresh interoperability check.
                        The recheck — not a manual edit — moved the state to TECHNICALLY ACCEPTED.
                    </p>
                </div>
            )}

            {(stage === "accepted" || done) && (
                <LocalBoundary testId="install-release-card" highlight={stage === "accepted"}>
                    <div className="mt-0 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900">
                            Clinical Service Owner — release-to-service authority
                        </p>
                        <StatusChip tone={done ? chip : "blue"}>
                            {done ? chipLabel : "DECISION REQUIRED"}
                        </StatusChip>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                        A different accountable role from the engineer who installed the
                        change. {AI_LINE}
                    </p>
                    <ul className="mt-3 grid grid-cols-2 gap-1 text-sm text-slate-600 sm:grid-cols-3">
                        {CLINICAL_REVIEW.map((c) => (
                            <li key={c} className="flex items-center gap-1.5">
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" strokeWidth={1.5} />
                                {c}
                            </li>
                        ))}
                    </ul>
                    {scenario === "denied" && !done && (
                        <p className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="release-gate-note">
                            Gate: no accepted service window and no validated clinical
                            contingency — release-to-service authority is withheld.
                        </p>
                    )}
                    {observationNote && !done && (
                        <p className="mt-3 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900" data-testid="observation-note">
                            Additional observation window recorded — release deferred until
                            the observation evidence is reviewed.
                        </p>
                    )}
                    {!done && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            <ActionBtn
                                primary
                                onClick={() => setStage("released")}
                                disabled={scenario === "denied"}
                                testId="release-to-service-btn"
                            >
                                Release to service
                            </ActionBtn>
                            <ActionBtn onClick={() => setObservationNote(true)} testId="request-observation-btn">
                                Request additional observation
                            </ActionBtn>
                            <ActionBtn onClick={() => setStage("rolledback")} testId="rollback-btn">
                                Roll back
                            </ActionBtn>
                            <ActionBtn onClick={() => setStage("denied")} testId="deny-release-btn">
                                Deny release
                            </ActionBtn>
                        </div>
                    )}
                </LocalBoundary>
            )}

            {stage === "released" && scenario === "rollback" && (
                <div className="mt-4 rounded-sm border border-red-200 bg-red-50 p-6" data-testid="post-install-failure-panel">
                    <p className="font-mono text-sm font-medium text-red-700">
                        POST-INSTALLATION HEALTH CHECK FAILED DURING OBSERVATION
                    </p>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-red-900/70">
                        The revised post-installation health check fails after release. The
                        approved rollback procedure — staged before installation began —
                        withdraws the release under provider authority.
                    </p>
                    <div className="mt-4">
                        <ActionBtn primary onClick={() => setStage("rolledback")} testId="execute-rollback-btn">
                            Execute approved rollback — provider authority
                        </ActionBtn>
                    </div>
                </div>
            )}

            {done && (
                <div
                    className={`mt-4 rounded-sm border p-6 ${
                        stage === "released" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
                    }`}
                    data-testid="install-final-banner"
                >
                    <p className={`font-mono text-sm font-medium ${stage === "released" ? "text-green-700" : "text-red-700"}`}>
                        {chipLabel}
                    </p>
                    <p className={`mt-2 max-w-2xl text-sm leading-relaxed ${stage === "released" ? "text-green-900/70" : "text-red-900/70"}`}>
                        {stage === "released" &&
                            "The equipment is back in clinical use. A technical and operational release receipt now binds the tests, the exception history and both acceptances to this decision."}
                        {stage === "denied" &&
                            "An authorised role determined that this release must not proceed at this site and time. The denial is itself an attributable, receipted decision — not a silent stall."}
                        {stage === "rolledback" &&
                            "The release was withdrawn under the approved rollback procedure. The device returns to its previous verified state and the failure evidence travels upstream as a bounded receipt."}
                    </p>
                </div>
            )}
        </section>
    );
};
